import React, { useState, useEffect } from 'react';
import { Grid, Dialog, DialogTitle } from '@mui/material';
import { GetApp } from '@mui/icons-material';
import { formatShortMonthDayYear } from '../../../shared/Dates';
import { client, consoleLogInDev, formatCurrency } from 'shared';
import download from 'shared/Download';
import PaySectionHeader from './PaySectionHeader';
import { PayBackgroundPaper } from './PayPapers';
import { Colors, IconButton, OutlinedDropdown, PrimaryButton, SecondaryButton, Text } from 'library';
import { Table, TableColumn } from 'library/ui';
import { useUserContext } from 'UserContext';

interface ILineItem {
    amount: number;
}
interface IInvoice {
    id: number;
    amount: number;
    amount_paid?: number;
    paid_date: string;
    line_items: ILineItem[];
    bill_date: string;
}

const COMPONENT_STATES = {
    neutral: 'neutral',
    submittingPayment: 'submittingPayment',
    confirmSubmitPayment: 'confirmSubmitPayment',
    downloading: 'downloading',
};

const FailedPayments = () => {
    const [componentState, setComponentState] = useState(COMPONENT_STATES.neutral);
    const [failedPayments, setFailedPayments] = useState<IInvoice[]>([]);
    const [selectedFailedPayment, setSelectedFailedPayment] = useState<number>();
    const [selectedPaymentInfo, setSelectedPaymentInfo] = useState<number>();
    const [errorMessage, setErrorMessage] = useState('');
    const { user, role } = useUserContext();

    const isBusiness = role === 'business_active';

    useEffect(() => {
        getFailedPayments();
    }, []);

    const getFailedPayments = () => {
        client('payment/api/invoice/?status=overdue&size=100')
            .then((response) => {
                setFailedPayments(response.results);
            })
            .catch(consoleLogInDev);
    };

    const onSelectFailedPayment = (id: number) => {
        setSelectedFailedPayment(id);
        setComponentState(COMPONENT_STATES.confirmSubmitPayment);
    };

    const onRetryPayment = () => {
        setComponentState(COMPONENT_STATES.submittingPayment);
        setErrorMessage('');

        client(`payment/api/invoice/${selectedFailedPayment}/retry/`, {
            body: { payment_information_id: selectedPaymentInfo },
        })
            .then(() => {
                setFailedPayments(failedPayments.filter((x) => x.id !== selectedFailedPayment));
                setSelectedFailedPayment(undefined);
                setSelectedPaymentInfo(undefined);
            })
            .catch((error) => {
                setErrorMessage(error.message || 'There was an error retrying your payment');
            })
            .finally(() => setComponentState(COMPONENT_STATES.neutral));
    };

    const onDialogClose = () => {
        setComponentState(COMPONENT_STATES.neutral);
        setSelectedPaymentInfo(undefined);
    };

    const onDownloadClick = (invoiceId: number) => {
        setComponentState(COMPONENT_STATES.downloading);
        setErrorMessage('');
        download(`payment/api/invoice/${invoiceId}/download/`)
            .catch((error) => {
                consoleLogInDev(error);
                setErrorMessage('There was an error generating your invoice');
            })
            .finally(() => {
                setComponentState(COMPONENT_STATES.neutral);
            });
    };

    if (!failedPayments?.length) {
        return <></>;
    }

    const selectOptions = [
        { key: 0, value: 'Select One' },
        ...(user?.payment_information || []).map((info) => {
            const infoData = info.data;
            return {
                key: info.id,
                value: `Account ending in ${infoData?.last4 || info.last_four}`,
            };
        }),
    ];

    const failedTotal = failedPayments.reduce((sum, item) => sum + item.amount - (item.amount_paid || 0), 0);

    const columns: TableColumn<IInvoice>[] = [
        { key: 'id', label: 'ID', render: (row) => row.id },
        {
            key: 'date',
            label: 'Payment Date',
            render: (row) => formatShortMonthDayYear(row.bill_date || row.paid_date),
        },
        {
            key: 'amount',
            label: `Amount Owed (${formatCurrency(failedTotal)})`,
            render: (row) => `$${(row.amount - (row.amount_paid || 0)).toFixed(2)}`,
        },
        {
            key: 'download',
            label: '',
            render: (row) =>
                isBusiness ? (
                    <IconButton
                        icon={GetApp}
                        tooltip="Download"
                        disabled={componentState === COMPONENT_STATES.downloading}
                        onClick={() => onDownloadClick(row.id)}
                    />
                ) : null,
        },
        {
            key: 'retry',
            label: '',
            render: (row) => (
                <PrimaryButton
                    loading={componentState === COMPONENT_STATES.submittingPayment}
                    disabled={componentState === COMPONENT_STATES.submittingPayment}
                    onClick={() => onSelectFailedPayment(row.id)}
                    buttonStyle={{ width: 'auto' }}
                >
                    Retry
                </PrimaryButton>
            ),
        },
    ];

    return (
        <Grid item xs={12}>
            <PayBackgroundPaper>
                <PaySectionHeader textStyle={{ color: Colors.error }}>Failed Payments</PaySectionHeader>
                <Text variant="body1">
                    It looks like there was an issue with your payment. Please update your default payment method, add
                    another payment method, or verify there are enough funds in your default account to cover the amount
                    before retrying the payment.
                </Text>
                <Grid style={{ marginBottom: 10, marginTop: 10 }}>
                    <Table columns={columns} data={failedPayments} />
                </Grid>
                {errorMessage && (
                    <Text variant="body1" textStyle={{ color: Colors.error }}>
                        {errorMessage}
                    </Text>
                )}
            </PayBackgroundPaper>
            <Dialog open={componentState === COMPONENT_STATES.confirmSubmitPayment} onClose={onDialogClose}>
                <DialogTitle id="simple-dialog-title">Pay Invoice</DialogTitle>
                <Grid container style={{ padding: 10 }}>
                    <Grid item style={{ padding: 10, marginBottom: 10 }}>
                        <Text variant="body1">Select payment method</Text>
                        <OutlinedDropdown
                            value={selectedPaymentInfo || 0}
                            fields={selectOptions}
                            onChange={(event) => setSelectedPaymentInfo(event.target.value)}
                        />
                    </Grid>
                    <Grid container item justifyContent="space-evenly" style={{ marginBottom: 10 }}>
                        <Grid item xs={4}>
                            <SecondaryButton onClick={onDialogClose}>Cancel</SecondaryButton>
                        </Grid>
                        <Grid item xs={4}>
                            <PrimaryButton
                                disabled={!selectedPaymentInfo || componentState === COMPONENT_STATES.submittingPayment}
                                onClick={onRetryPayment}
                            >
                                OK
                            </PrimaryButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Dialog>
        </Grid>
    );
};

export default FailedPayments;
